import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { ExternalTool } from '@tunasong/manifest'
import { Add, Delete, OpenLink } from '@tunasong/icons'
import { useUserConfig } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'

const defaultTool: ExternalTool = { name: '', url: '' }

const normalizeUrl = (url: string): string => {
  const trimmed = url.trim()
  if (trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`
  }
  return trimmed
}

const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return /^https?:$/i.test(parsed.protocol)
  } catch {
    return false
  }
}

export const ExternalToolsConfig: FC = () => {
  const [externalTools = [], updateExternalTools] = useUserConfig('externalTools')
  const [newTool, setNewTool] = useState<ExternalTool>(defaultTool)
  const [urlError, setUrlError] = useState<string | null>(null)

  const handleAdd = () => {
    if (!newTool.name.trim() || !newTool.url.trim()) {
      return
    }
    const normalized = { ...newTool, url: normalizeUrl(newTool.url) }
    if (!isValidUrl(normalized.url)) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)')
      return
    }
    setUrlError(null)
    updateExternalTools([...externalTools, normalized])
    setNewTool(defaultTool)
  }

  const handleRemove = (index: number) => {
    updateExternalTools(externalTools.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ my: 2 }}>
        External Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add links to external tools and services. These will appear in the Tools section of the sidebar for quick access.
      </Typography>

      {externalTools.length > 0 ? (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {externalTools.map((tool, index) => (
              <TableRow key={index}>
                <TableCell>{tool.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tool.url}
                    </Typography>
                    <IconButton size="small" href={tool.url} target="_blank" rel="noopener noreferrer">
                      <OpenLink fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleRemove(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No external tools added yet.
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Name"
          placeholder="e.g., My DAW"
          value={newTool.name}
          onChange={e => setNewTool({ ...newTool, name: e.target.value })}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="URL"
          placeholder="https://example.com"
          value={newTool.url}
          error={!!urlError}
          helperText={urlError}
          onChange={e => {
            setNewTool({ ...newTool, url: e.target.value })
            if (urlError) setUrlError(null)
          }}
          sx={{ flex: 2 }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={!newTool.name.trim() || !newTool.url.trim()}
        >
          Add
        </Button>
      </Stack>
    </Box>
  )
}

export default ExternalToolsConfig
